import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import {
  UpgradePlanModalButton,
  provenanceSubmitButtonClassNames,
} from "../_provenance/-components";
import VoiceUploadSection from "./-components/VoiceUploadSection";
import type {
  AudioAnalysisResult,
  SubmitVoices,
  Voice,
  VoiceFile,
} from "@/lib/types/vexon";
import { invariant } from "@/lib/invariant";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/http";
import { userPlanQueryOptions } from "@/lib/queries/payment/userPlan";
import { useFFmpeg } from "@/lib/hooks/ffmpeg";

export const Route = createFileRoute("/_app/vexon/")({
  component: VexonVoiceDetector,
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(userPlanQueryOptions);
  },
});

const audioMimeType = "audio/webm;codecs=opus" as const;
const audioFileExt = "webm" as const;

function VexonVoiceDetector() {
  const plan = useSuspenseQuery(userPlanQueryOptions);

  const navigate = Route.useNavigate();

  const [referenceVoice, setReferenceVoice] = useState<VoiceFile | null>(null);
  const [verifyVoice, setVerifyVoice] = useState<VoiceFile | null>(null);

  const [recordingVoice, setRecordingVoice] = useState<Voice | null>(null);
  const [playingVoice, setPlayingVoice] = useState<Voice | null>(null);

  // invalidate on leave without actually blocking
  useBlocker({
    shouldBlockFn({ next }) {
      if (next.routeId !== "/_app/vexon/result") {
        if (referenceVoice) URL.revokeObjectURL(referenceVoice.url);
        if (verifyVoice) URL.revokeObjectURL(verifyVoice.url);
      }
      return false;
    },
    enableBeforeUnload: false,
  });

  const { ffmpegAsync } = useFFmpeg();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const handleReferenceFileChange = (file: File) => {
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File size is too large");
      return;
    }
    setReferenceVoice((old) => {
      if (old?.url) URL.revokeObjectURL(old.url);
      const url = URL.createObjectURL(file);
      return { file, url };
    });
  };

  const handleVerifyFileChange = (file: File) => {
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File size is too large");
      return;
    }
    setVerifyVoice((old) => {
      if (old?.url) URL.revokeObjectURL(old.url);
      const url = URL.createObjectURL(file);
      return { file, url };
    });
  };

  const toggleReferencePlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "reference" ? null : "reference";
    });
  };

  const toggleVerifyPlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "verify" ? null : "verify";
    });
  };

  const startRecording = async (type: "reference" | "verify") => {
    try {
      if (recordingVoice !== null) {
        stopRecording();

        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const audioChunks: Array<Blob> = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: audioMimeType,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, {
          type: audioMimeType,
        });

        const fileNamePrefix = `recorded-audio-${type}-${Date.now()}`;
        const inputFileName = `${fileNamePrefix}.${audioFileExt}`;
        const outputFileName = `${fileNamePrefix}.wav`;

        const audioFile = new File([audioBlob], inputFileName, {
          type: audioMimeType,
        });

        const ffmpeg = await ffmpegAsync;
        await ffmpeg.writeFile(
          inputFileName,
          await audioFile.arrayBuffer().then((buff) => new Uint8Array(buff)),
        );

        await ffmpeg.exec(["-i", inputFileName, outputFileName]);

        const outputData = (await ffmpeg.readFile(
          outputFileName,
        )) as Uint8Array;
        void ffmpeg.deleteFile(inputFileName);

        const outputBlob = new Blob([outputData], {
          type: "audio/wav",
        });
        const outputFile = new File([outputBlob], outputFileName, {
          type: "audio/wav",
        });

        if (type === "reference") {
          setReferenceVoice((old) => {
            if (old?.url) URL.revokeObjectURL(old.url);
            const url = URL.createObjectURL(outputFile);
            return { file: outputFile, url };
          });
        } else {
          setVerifyVoice((old) => {
            if (old?.url) URL.revokeObjectURL(old.url);
            const url = URL.createObjectURL(outputFile);
            return { file: outputFile, url };
          });
        }
        setRecordingVoice(null);

        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;

      setRecordingVoice(type);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error(
        "Could not access microphone. Please allow microphone access.",
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    } else {
      console.warn(
        "stopped recording that was already inactive",
        mediaRecorderRef.current,
      );
    }
  };

  const startRecordingReference = () => {
    setPlayingVoice(null);
    startRecording("reference");
  };
  const startRecordingVerify = () => {
    setPlayingVoice(null);
    startRecording("verify");
  };

  const resetReferenceVoice = () => {
    setPlayingVoice(null);
    setReferenceVoice((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const resetVerifyVoice = () => {
    setPlayingVoice(null);
    setVerifyVoice((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const handleAnalyze = useMutation({
    async mutationFn(voices: SubmitVoices) {
      const formData = new FormData();
      if (voices.reference) formData.append("file1", voices.reference.file);
      formData.append("file2", voices.verify.file);
      const response = await api.detector
        .post<AudioAnalysisResult>("v1/vexon/audio-analysis", {
          body: formData,
        })
        .json();

      return response;
    },
    onSuccess(data, voices) {
      if (voices.reference && !voices.reference.url)
        voices.reference.url = URL.createObjectURL(voices.reference.file);
      if (!voices.verify.url)
        voices.verify.url = URL.createObjectURL(voices.verify.file);

      navigate({
        to: "/vexon/result",
        state: {
          vexonResult: data,
          vexonAudio: voices,
        },
        mask: {
          to: ".",
        },
      });
    },
    onError(error) {
      if (!(error instanceof HTTPError)) {
        toast.error("File size is too large");
        return;
      }

      if (error.response.status === 413) {
        toast.error("File size is too large");
      } else if (error.response.status === 403) {
        toast.error("Reached the maximum number of requests");
      } else {
        toast.error("An error occurred while analyzing voices");
      }
    },
  });

  if (handleAnalyze.isPending) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="text-lg">Analyzing voices...</span>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-gray-200 p-6 dark:border-gray-800">
      <div className="flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Vexon Voice Detector</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Polygraf AI's Voice Verification System
            </p>
          </div>
          {/* <Button variant="outline" className="flex items-center gap-2">
            <History size={16} />
            <span>Detection History</span>
          </Button> */}
        </div>

        <div className="space-y-6">
          <VoiceUploadSection
            title="Reference Voice (optional)"
            description="Voice to be taken as reference"
            voiceFile={referenceVoice}
            onFileChange={handleReferenceFileChange}
            onPlayPause={toggleReferencePlayPause}
            isRecording={recordingVoice === "reference"}
            isPlaying={playingVoice === "reference"}
            onStartRecording={startRecordingReference}
            onStopRecording={stopRecording}
            onReset={resetReferenceVoice}
          />

          <VoiceUploadSection
            title="Voice to Verify"
            description="Voice to be verified"
            voiceFile={verifyVoice}
            onFileChange={handleVerifyFileChange}
            onPlayPause={toggleVerifyPlayPause}
            isRecording={recordingVoice === "verify"}
            isPlaying={playingVoice === "verify"}
            onStartRecording={startRecordingVerify}
            onStopRecording={stopRecording}
            onReset={resetVerifyVoice}
          />

          <div className="flex justify-end">
            {plan.data.info?.plan.features.available_services.includes(
              "VEXON",
            ) ? (
              <Button
                className="px-8"
                onClick={() => {
                  invariant(
                    verifyVoice,
                    "submit button should not be enabled without a verifyVoice",
                  );
                  const voices: SubmitVoices = { verify: verifyVoice };
                  if (referenceVoice) voices.reference = referenceVoice;
                  handleAnalyze.mutate(voices);
                }}
                disabled={!verifyVoice}
              >
                Analyze
              </Button>
            ) : (
              <UpgradePlanModalButton
                className={provenanceSubmitButtonClassNames}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
